import type { Order_By } from '@graphql/__generated__/graphql';

// URL-driven state for the server-paginated list views. `?where` and `?order_by`
// are deep-linkable, bookmarkable and hand-editable, so anything read from them
// is untrusted input: a malformed value must never throw during render (blank
// page / error boundary) nor reach Hasura in a shape that changes the query's
// meaning.

// `JSON.parse` on a URL param, with a fallback instead of a synchronous throw.
// `JSON.parse('null')` succeeds and yields null, which would break a query
// expecting an object, so null collapses to the fallback too.
export const parseJsonParam = <T>(raw: string | null, fallback: T): T => {
  if (raw == null) {
    return fallback;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed == null ? fallback : (parsed as T);
  } catch {
    return fallback;
  }
};

// The `?where` param carries the quick-filter selections between ListToolbar
// and each list view, whose consumers need a plain object: a valid non-object
// payload degrades to no filter, like malformed JSON does.
export const parseWhereParam = (
  raw: string | null,
): Record<string, unknown> => {
  const parsed = parseJsonParam<unknown>(raw, {});
  return typeof parsed === 'object' && parsed != null && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : {};
};

// Hasura's direction set, spelled out rather than read off the generated enum at
// runtime: `Order_By` lives in the codegen bundle, which is gitignored and only
// exists after `pnpm codegen`. Importing it as a value made this module — and so
// its test — unresolvable in the CI Jest job, which runs without a codegen step.
// The type-only import above still ties the list to the schema, in both
// directions: `satisfies` rejects an entry the enum no longer has, and
// `_directionsMatch` below rejects an enum member the list is missing.
const DIRECTIONS = [
  'asc',
  'asc_nulls_first',
  'asc_nulls_last',
  'desc',
  'desc_nulls_first',
  'desc_nulls_last',
] as const satisfies readonly `${Order_By}`[];

// Assigned, not merely declared: a type alias that resolves to `never` is legal
// TypeScript and emits nothing, so the check only bites once a value has to be
// assignable to it. If Order_By gains a direction, this stops compiling.
const _directionsMatch: Exclude<
  `${Order_By}`,
  (typeof DIRECTIONS)[number]
> extends never
  ? true
  : never = true;

// Widened for lookups: `includes` on the literal tuple would reject a plain string.
const DIRECTION_VALUES: readonly string[] = DIRECTIONS;

// A direction is a plain enum value (`{ created_at: 'desc' }`). One nesting
// level is allowed only for keys the caller declares as relationships, so a
// dotted grid column works (`{ user: { first_name: 'asc' } }`) while a scalar
// column cannot smuggle an object through: `{ created_at: { x: 'asc' } }` would
// otherwise reach Hasura and come back as a raw `field "x" not found` printed
// by the error Alert, which is what this guard exists to prevent.
const isDirection = (
  value: unknown,
  // null when the key is a scalar column: no nesting allowed at all. Otherwise
  // the exact inner columns the relationship exposes as sortable.
  allowedNestedKeys: string[] | null,
): boolean => {
  if (typeof value === 'string') {
    // A relationship key must nest. `{ user: 'asc' }` is a scalar direction on a
    // relationship, which types as `users_order_by`, not the direction enum — so
    // it fails GraphQL validation and blanks the list behind the raw error this
    // guard exists to prevent, exactly like an unknown inner key does.
    return allowedNestedKeys == null && DIRECTION_VALUES.includes(value);
  }
  if (allowedNestedKeys != null && typeof value === 'object' && value != null) {
    const entries = Object.entries(value as Record<string, unknown>);
    return (
      entries.length > 0 &&
      entries.every(
        ([innerKey, inner]) =>
          // The inner KEY matters as much as the direction: validating only the
          // direction let `{ user: { bogus: 'asc' } }` through, which reaches
          // Hasura, fails GraphQL validation and blanks the list behind the raw
          // `field "bogus" not found` this guard exists to prevent.
          allowedNestedKeys.includes(innerKey) &&
          typeof inner === 'string' &&
          DIRECTION_VALUES.includes(inner),
      )
    );
  }
  return false;
};

type SanitizeOptions<T> = {
  /** Top-level keys the grid actually exposes as sortable headers. */
  sortableKeys: string[];
  /**
   * Relationship keys that may carry one nesting level (a dotted grid column
   * such as `user.first_name`), mapped to the inner columns each one actually
   * exposes: `{ user: ['first_name'] }`. Any key not listed here must carry a
   * plain enum direction, and any inner key not listed for its relationship is
   * rejected rather than forwarded to Hasura.
   */
  nestedKeys?: Record<string, string[]>;
  /** Used when nothing valid survives. */
  fallback: T[];
  /** Appended as the last sort key unless already present — see `withTiebreaker`. */
  tiebreaker: T;
  /** Key carried by `tiebreaker`, to detect it if the caller already sorts on it. */
  tiebreakerKey: string;
};

export const sanitizeOrderBy = <T>(
  raw: string | null,
  {
    sortableKeys,
    nestedKeys,
    fallback,
    tiebreaker,
    tiebreakerKey,
  }: SanitizeOptions<T>,
): T[] => {
  const parsed = parseJsonParam<T[]>(raw, []);
  const entries = (Array.isArray(parsed) ? parsed : []).filter(entry => {
    const keys = Object.keys((entry ?? {}) as Record<string, unknown>);
    // `every` is vacuously true on `{}`, so a keyless entry would pass and reach
    // Hasura as an order-less query — the same non-determinism as no sort.
    return (
      keys.length > 0 &&
      keys.every(
        key =>
          sortableKeys.includes(key) &&
          isDirection(
            (entry as Record<string, unknown>)[key],
            nestedKeys?.[key] ?? null,
          ),
      )
    );
  });
  const base = entries.length > 0 ? entries : fallback;
  return withTiebreaker(base, tiebreaker, tiebreakerKey);
};

// Appends `tiebreaker` as the last sort key unless the sort already carries
// `tiebreakerKey`. `tiebreaker` must be unique (a primary key): without it a
// low-cardinality sort — `created_at` after a bulk import, for instance — leaves
// ties whose order is undefined, and `LIMIT`/`OFFSET` can then repeat or skip
// rows between pages because a bounded top-N sort may break ties differently
// per offset.
export const withTiebreaker = <T>(
  orderBy: T[],
  tiebreaker: T,
  tiebreakerKey: string,
): T[] =>
  orderBy.some(entry =>
    Object.prototype.hasOwnProperty.call(entry ?? {}, tiebreakerKey),
  )
    ? orderBy
    : [...orderBy, tiebreaker];

const isSortEntry = (value: unknown): value is object =>
  value != null && typeof value === 'object' && !Array.isArray(value);

// The `?order_by` param as the list the query will send. `[]` — what the grid
// writes when the user clears the sort — means the default sort, not "no
// sort"; a bare object is kept as the one-element list Hasura would coerce it
// to; non-object entries are dropped; anything else (missing, malformed, null,
// a primitive) is the default.
export const parseOrderByParam = <T>(
  raw: string | null,
  fallback: T[],
): T[] => {
  const parsed = parseJsonParam<unknown>(raw, null);
  const entries = Array.isArray(parsed)
    ? parsed.filter(isSortEntry)
    : isSortEntry(parsed)
    ? [parsed]
    : [];
  return entries.length > 0 ? (entries as T[]) : fallback;
};
